/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PolicyForm.tsx
 *     Description     :   Form component for creating/editing maintenance policies
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-19
 *     Modified        :   2026-01-19 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.11.0 (Specification Testing Standards Edition)
 *     Feature         :   028-lakehouse-maintenance
 * =========================================================================================
 */

import React, { useState, useEffect } from 'react';
import { AppModal } from '../ui/AppModal';
import { Button, Input, Textarea, Select, Alert, Toggle } from '../ui';
import { useCreatePolicy, useUpdatePolicy } from '../../hooks/useMaintenanceData';
import type { MaintenancePolicy } from '../../../../shared/types/maintenance';

interface PolicyFormProps {
  open: boolean;
  onClose: () => void;
  policy?: Partial<MaintenancePolicy>;
  mode: 'create' | 'edit';
}

interface PolicyFormData {
  name: string;
  description: string;
  optimizeEnabled: boolean;
  optimizeFrequencyHours: number;
  vacuumEnabled: boolean;
  vacuumFrequencyHours: number;
  vacuumRetentionDays: number;
  zorderEnabled: boolean;
  zorderColumns?: string;
  maintenanceWindowStart?: string;
  maintenanceWindowEnd?: string;
  priority: number;
}

export const PolicyForm: React.FC<PolicyFormProps> = ({ open, onClose, policy, mode }) => {
  const createMutation = useCreatePolicy();
  const updateMutation = useUpdatePolicy();

  // SC-008: Track timing for policy configuration (target <2 minutes)
  const [formOpenTime, setFormOpenTime] = useState<number | null>(null);

  const [formData, setFormData] = useState<PolicyFormData>({
    name: '',
    description: '',
    optimizeEnabled: true,
    optimizeFrequencyHours: 24,
    vacuumEnabled: false,
    vacuumFrequencyHours: 168,
    vacuumRetentionDays: 7,
    zorderEnabled: false,
    zorderColumns: '',
    maintenanceWindowStart: undefined,
    maintenanceWindowEnd: undefined,
    priority: 3,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof PolicyFormData, string>>>({});

  // Track when form is opened (SC-008: UX timing instrumentation)
  useEffect(() => {
    if (open) {
      setFormOpenTime(Date.now());
    }
  }, [open]);

  useEffect(() => {
    if (policy && mode === 'edit') {
      setFormData({
        name: policy.name || '',
        description: policy.description || '',
        optimizeEnabled: policy.optimizeThresholdPct !== undefined ? policy.optimizeThresholdPct > 0 : true,
        optimizeFrequencyHours: 24, // Not in MaintenancePolicy, use default
        vacuumEnabled: policy.vacuumRetentionDays !== undefined && policy.vacuumRetentionDays > 0,
        vacuumFrequencyHours: 168, // Not in MaintenancePolicy, use default
        vacuumRetentionDays: policy.vacuumRetentionDays || 7,
        zorderEnabled: false, // Not in MaintenancePolicy
        zorderColumns: '',
        maintenanceWindowStart: policy.maintenanceStartTime || undefined,
        maintenanceWindowEnd: policy.maintenanceEndTime || undefined,
        priority: 3, // Not in MaintenancePolicy, use default
      });
    }
  }, [policy, mode]);

  const handleChange = (field: keyof PolicyFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
  ) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.type === 'number'
        ? Number(event.target.value)
        : event.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Boolean (Toggle) fields — kit Toggle reports the next checked state directly.
  const handleToggle = (field: keyof PolicyFormData) => (checked: boolean) => {
    setFormData((prev) => ({ ...prev, [field]: checked }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Numeric select (priority) — preserve the number type the form state expects.
  const handlePriorityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(event.target.value);
    setFormData((prev) => ({ ...prev, priority: value }));
    setErrors((prev) => ({ ...prev, priority: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PolicyFormData, string>> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Policy name is required';
    }

    if (formData.optimizeEnabled && formData.optimizeFrequencyHours < 1) {
      newErrors.optimizeFrequencyHours = 'Frequency must be at least 1 hour';
    }

    if (formData.vacuumEnabled) {
      if (formData.vacuumFrequencyHours < 1) {
        newErrors.vacuumFrequencyHours = 'Frequency must be at least 1 hour';
      }
      if (formData.vacuumRetentionDays < 0) {
        newErrors.vacuumRetentionDays = 'Retention cannot be negative';
      }
    }

    if (
      formData.maintenanceWindowStart &&
      formData.maintenanceWindowEnd &&
      formData.maintenanceWindowStart >= formData.maintenanceWindowEnd
    ) {
      newErrors.maintenanceWindowEnd = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      // Map form data to MaintenancePolicy format
      const policyData: Partial<MaintenancePolicy> = {
        name: formData.name,
        description: formData.description || null,
        optimizeThresholdPct: formData.optimizeEnabled ? 20 : 0, // Default threshold
        vacuumRetentionDays: formData.vacuumEnabled ? formData.vacuumRetentionDays : 0,
        maintenanceStartTime: formData.maintenanceWindowStart || null,
        maintenanceEndTime: formData.maintenanceWindowEnd || null,
        isActive: true,
      };

      if (mode === 'create') {
        await createMutation.mutateAsync(policyData);
      } else if (policy?.id) {
        await updateMutation.mutateAsync({ id: policy.id, policy: policyData });
      }

      // SC-008: Log policy configuration timing
      if (formOpenTime) {
        const durationMs = Date.now() - formOpenTime;
        console.log('[UX Timing] Policy configuration completed', {
          mode,
          durationMs,
          durationSeconds: Math.round(durationMs / 1000),
          targetMs: 120000, // 2 minutes
          withinTarget: durationMs < 120000,
        });

        // Log to backend for metrics (optional)
        if (durationMs > 120000) {
          console.warn('[UX Timing] Policy configuration exceeded 2-minute target', {
            durationMs,
            durationMinutes: Math.round(durationMs / 60000),
          });
        }
      }

      onClose();
    } catch (error) {
      console.error('Policy save error:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      optimizeEnabled: true,
      optimizeFrequencyHours: 24,
      vacuumEnabled: false,
      vacuumFrequencyHours: 168,
      vacuumRetentionDays: 7,
      zorderEnabled: false,
      zorderColumns: '',
      maintenanceWindowStart: undefined,
      maintenanceWindowEnd: undefined,
      priority: 3,
    });
    setErrors({});
    onClose();
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <AppModal
      title={mode === 'create' ? 'Create New Policy' : 'Edit Policy'}
      open={open}
      onClose={handleClose}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'Saving...' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-4">
          {error.message || 'Failed to save policy'}
        </Alert>
      )}

      <div className="mt-2 flex flex-col gap-4">
        {/* Basic Info */}
        <Input
          label="Policy Name"
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          errorMessage={errors.name}
          required
          isFullWidth
        />

        <Textarea
          label="Description"
          value={formData.description}
          onChange={handleChange('description')}
          rows={2}
          isFullWidth
        />

        <Select
          label="Priority"
          value={formData.priority}
          onChange={handlePriorityChange}
          isFullWidth
        >
          <option value={1}>P1 - Critical</option>
          <option value={2}>P2 - High</option>
          <option value={3}>P3 - Medium</option>
          <option value={4}>P4 - Low</option>
        </Select>

        {/* OPTIMIZE Settings */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <Toggle
            checked={formData.optimizeEnabled}
            onChange={handleToggle('optimizeEnabled')}
            label="Enable OPTIMIZE"
          />
          {formData.optimizeEnabled && (
            <Input
              label="OPTIMIZE Frequency (hours)"
              type="number"
              value={formData.optimizeFrequencyHours}
              onChange={handleChange('optimizeFrequencyHours')}
              error={!!errors.optimizeFrequencyHours}
              errorMessage={errors.optimizeFrequencyHours}
              isFullWidth
              className="mt-2"
            />
          )}
        </div>

        {/* VACUUM Settings */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <Toggle
            checked={formData.vacuumEnabled}
            onChange={handleToggle('vacuumEnabled')}
            label="Enable VACUUM"
          />
          {formData.vacuumEnabled && (
            <>
              <Input
                label="VACUUM Frequency (hours)"
                type="number"
                value={formData.vacuumFrequencyHours}
                onChange={handleChange('vacuumFrequencyHours')}
                error={!!errors.vacuumFrequencyHours}
                errorMessage={errors.vacuumFrequencyHours}
                isFullWidth
                className="mt-2"
              />
              <Input
                label="VACUUM Retention (days)"
                type="number"
                value={formData.vacuumRetentionDays}
                onChange={handleChange('vacuumRetentionDays')}
                error={!!errors.vacuumRetentionDays}
                errorMessage={errors.vacuumRetentionDays}
                helperText={errors.vacuumRetentionDays ? undefined : 'Minimum 0, recommended 7+'}
                isFullWidth
                className="mt-2"
              />
            </>
          )}
        </div>

        {/* Z-ORDER Settings */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <Toggle
            checked={formData.zorderEnabled}
            onChange={handleToggle('zorderEnabled')}
            label="Enable Z-ORDER"
          />
          {formData.zorderEnabled && (
            <Input
              label="Z-ORDER Columns (comma-separated)"
              value={formData.zorderColumns}
              onChange={handleChange('zorderColumns')}
              placeholder="date, region, category"
              isFullWidth
              className="mt-2"
            />
          )}
        </div>

        {/* Maintenance Window */}
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
          <p className="text-sm text-[var(--color-text-secondary)]">Maintenance Window (optional)</p>
          <div className="mt-2 flex gap-4">
            <Input
              label="Start Time"
              type="time"
              value={formData.maintenanceWindowStart || ''}
              onChange={handleChange('maintenanceWindowStart')}
              isFullWidth
            />
            <Input
              label="End Time"
              type="time"
              value={formData.maintenanceWindowEnd || ''}
              onChange={handleChange('maintenanceWindowEnd')}
              error={!!errors.maintenanceWindowEnd}
              errorMessage={errors.maintenanceWindowEnd}
              isFullWidth
            />
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default PolicyForm;
